import React, { useState } from 'react';
import { useActions } from 'hooks/useActions';
import { actions as authActions, selectors as authSelectors } from 'store/auth';
import { actions as venueActions } from 'store/venues';
import packageJSON from '../../../package.json';
import { getMatchPath } from 'utils/urlUtils';
import { Drawer, IconButton, Typography, Card, color, Tab, Tabs, Button, Box } from '@kistech/kis-ui';
import { Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { useStyles } from './styled';

// RETURNS THE PATHNAME WITHOUT THE KIOSKID
const pathInit = (path: string) => {
  const match = getMatchPath(path, '/kiosks/:kioskId?');

  // IF THERE IS A MATCH OF PATH AND THAT PATH HAS KIOSKID, SLICE IT OUT
  if (match && match.params.kioskId) return path.slice(0, path.lastIndexOf('/')).trim();
  return path;
};

export interface MenuPanelProps {
  isOpen: boolean;
  onClose: () => void;
  routePath: string;
}

export const MenuPanel = (props: MenuPanelProps) => {
  const { isOpen, onClose, routePath } = props;
  const { logout } = useActions({ logout: authActions.logout });
  const { resetVenueAction } = useActions({ resetVenueAction: venueActions.resetAction });
  const path = React.useMemo(() => pathInit(routePath), [routePath]);
  const [to, setTo] = React.useState<string>(path);
  const admin = useSelector(authSelectors.selectAdmin);
  const [readOnlyAdmin, setReadOnlyAdmin] = useState(false);
  const [globalAdmin, setGlobalAdmin] = useState(false);

  const classes = useStyles();

  React.useEffect(() => {
    if (!path.includes(to)) {
      setTo(path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  // check for user is Read-Only Admin or not, if yes hide Media center tab
  React.useEffect(() => {
    if (admin && admin.role === 'GlobalAdmin') {
      setGlobalAdmin(true);
    } else if (admin && admin.role === 'ReadOnlyAdmin') {
      setReadOnlyAdmin(true);
    }
  }, [admin]);

  const storeClearingAndLogout = () => {
    onClose();
    resetVenueAction();
    logout();
  };

  const openSupportLink = () => {
    window.open(`https://kistechnologies.atlassian.net/servicedesk/customer/portal/4`);
  };

  const onChangeRoute = () => {
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} hasBorderRadius anchor="left">
      <Grid container direction="column" className={classes.menuPanelContainer}>
        <Grid direction="row" className={classes.menuPanelHeader}>
          <Typography size={24} weight={600}>
            Menu
          </Typography>
          <IconButton
            name="exit_circle"
            appearance="transparent"
            size={32}
            onClick={onClose}
            data-testid="ticket-log-x"
          />
        </Grid>
        <Card.Divider color={color.BORDER} />
        <Grid container direction="column" className={classes.menuPanelTabContainer}>
          <Grid className={classes.menuPanelItemsBlock}>
            <Tabs value={to} onChange={onChangeRoute}>
              <Tab iconName="kiosks" label="Kiosks" to="/kiosks" />
              <Tab iconName="credential" label="Users" to="/users" />
              <Tab iconName="activity_log" iconViewBox="0 0 59 36" label="Activity Log" to="/activityLog" />
              <Tab iconName="ticket" iconViewBox="-5 0 59 36" label="Delivery Log" to="/deliveryLog" />
              {!readOnlyAdmin && <Tab iconName="media_center" label="Media Center" to="/mediaCenter" />}
              {!readOnlyAdmin && <Tab iconViewBox="0 0 24 24" iconName="settings" label="Settings" to="/settings" />}
              {globalAdmin && (
                <Tab iconViewBox="0 0 24 24" iconName="settings" label="Configuration" to="/configuration" />
              )}
            </Tabs>
          </Grid>
          <Grid className={classes.menuPanelFooter}>
            <Button size="small" onClick={openSupportLink} className={classes.menuPanelButton}>
              Support
            </Button>
            <Button size="small" onClick={storeClearingAndLogout} className={classes.menuPanelButton}>
              Log Out
            </Button>
            <Box margin="2 0 4 0">
              <Typography size={12} align="center">
                Version: {packageJSON.version}
              </Typography>
              <Typography size={12} align="center">
                ©KIS Technologies
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Drawer>
  );
};
